import * as _ from 'lodash';
import { Component, ChangeDetectionStrategy, Inject, ChangeDetectorRef } from '@angular/core';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { MAT_SNACK_BAR_DATA, MatSnackBarRef } from '@angular/material/snack-bar';
import { AudioDeviceService } from '@wephone-app-phone/services/audio-devices.service';
import { PhoneService } from '@wephone-app-phone/services';
import { IJsPhoneConfig, IAudioDevice } from '@jsphonelib';
import { BottomSheetChangeVolume } from '@wephone-app-phone/modals/settings/bottom-sheet-change-volume/bottom-sheet-change-volume.component';
import { _ti } from '@wephone-translation';
import { UIBlockerService } from '@wephone-utils';


export interface INotificationData {
  event: string;
  in_use?: boolean;
  device_name?: string;
  devices?: any[];
}

@Component({
  selector: 'audio-device-notification',
  templateUrl: 'audio-device-notification.component.html',
  styleUrls: ['./audio-device-notification.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AudioDeviceNotificationComponent {
  message: string;
  hasNewDevices = false;
  private hasClosed = false;

  constructor(
    @Inject(MAT_SNACK_BAR_DATA) public data: INotificationData,
    private snackBarRef: MatSnackBarRef<AudioDeviceNotificationComponent>,
    private phoneService: PhoneService,
    private bottomSheet: MatBottomSheet,
    private cdRef: ChangeDetectorRef

  ) {
    const devices: IAudioDevice[] = this.phoneService.getAudioDevices();
    const inputCount: number = _.filter(devices, dev => dev.is_input).length;
    const outputCount: number = _.filter(devices, dev => dev.is_output).length;
    const deviceName = !data.device_name || data.device_name === '*' ? _ti('audio_device.default_device') : data.device_name;

    if (data.event === 'lost') {
      if (inputCount === 0) {
        this.message = _ti('audio_device.no_input_device');
      } else if (outputCount === 0) {
        this.message = _ti('audio_device.no_output_device');
      } else {
        this.message = deviceName + ' ' + _ti('audio_device.change.device_lost');
      }
    } else if (data.event === 'found') {
      if (data.in_use) {
        this.message = deviceName + ' ' + _ti('audio_device.change.device_detected.in_use');
      } else {
        const deviceNames = _.chain(data.devices).map('name').uniqWith().value();
        this.message = _ti('audio_device.change.device_detected.new') + ' : ' + deviceNames.join();
      }
    } else if (data.event === 'error') {
      this.message = _ti('audio_device.change.error') + ' : ' + deviceName;
    }

    this.hasNewDevices = !!data.devices;
    this.snackBarRef.afterDismissed().subscribe(
      () => {
        this.hasClosed = true;
      }
    );
  }

  close(): void {
    this.snackBarRef.dismiss();
  }

  async useNewDevices(): Promise<void> {
    const devices = this.data.devices;
    const inputs = _.filter(devices, dev => dev.is_input === 1);
    const outputs = _.filter(devices, dev => dev.is_output === 1);

    const phoneConfig: IJsPhoneConfig = {};
    const deviceNames = [];
    if (inputs.length <= 1 && outputs.length <= 1) {
      if (inputs.length === 1) {
        phoneConfig.input_device = inputs[0].name;
        deviceNames.push(phoneConfig.input_device);
      }
      if (outputs.length === 1) {
        phoneConfig.output_device = outputs[0].name;
        phoneConfig.ring_device = outputs[0].name;
        deviceNames.push(phoneConfig.output_device);
      }

      this.message = 'Switching to new audio device...';
      this.hasNewDevices = false;
      this.cdRef.markForCheck();
      this.cdRef.detectChanges();

      await this.phoneService.applyPhoneConfig(phoneConfig);

      this.message = deviceNames.join() + _ti('audio_device.notification_component.new_devices_in_use');
      this.cdRef.markForCheck();
      this.cdRef.detectChanges();
      setTimeout(
        () => {
          if (!this.hasClosed) {
            this.close();
          }
        },
        3000
      );
    } else {
      this.openSettings();
    }
  }

  openSettings(): void {
    this.close();
    // Cannot call AudioDeviceService.openSettings here because of circular dependency
    this.bottomSheet.open(BottomSheetChangeVolume);
  }
}
